import { DATAVERSE_OPERATOR_MAP, fromFetchXml, toFetchXml, type FetchElement } from "@kodikas/fetchxml-ts";
import type {
  ConditionGroupDraft,
  ConditionDraft,
  JoinDraft,
  ParseFetchXmlDraftResult,
  QueryTarget,
} from "./types.js";

export interface BuildFetchXmlInput {
  rootEntityLogicalName: string;
  rootSelectedAttributes: string[];
  joins: JoinDraft[];
  conditionGroups: ConditionGroupDraft[];
  conditions: ConditionDraft[];
}

function makeId(prefix: string): string {
  return `${prefix}-${Math.random().toString(16).slice(2, 10)}-${Date.now().toString(16)}`;
}

export function buildFetchElement(input: BuildFetchXmlInput): FetchElement {
  const rootEntity: FetchElement = {
    name: "entity",
    attributes: { name: input.rootEntityLogicalName },
    children: [],
  };

  for (const attributeName of input.rootSelectedAttributes) {
    rootEntity.children.push({ name: "attribute", attributes: { name: attributeName }, children: [] });
  }

  const rootFilter = buildTargetFilterNode("root", input.conditionGroups, input.conditions);
  if (rootFilter) {
    rootEntity.children.push(rootFilter);
  }

  attachJoinChildren(rootEntity, "root", input.joins, input.conditionGroups, input.conditions);

  return {
    name: "fetch",
    attributes: {
      version: "1.0",
      mapping: "logical",
    },
    children: [rootEntity],
  };
}

function attachJoinChildren(
  parentNode: FetchElement,
  parentTarget: QueryTarget,
  joins: JoinDraft[],
  groups: ConditionGroupDraft[],
  conditions: ConditionDraft[],
): void {
  const childJoins = joins.filter((item) => item.parentTarget === parentTarget);

  for (const join of childJoins) {
    if (!join.targetEntityLogicalName || !join.fromAttribute || !join.toAttribute) {
      continue;
    }

    const linkEntity: FetchElement = {
      name: "link-entity",
      attributes: {
        name: join.targetEntityLogicalName,
        from: join.fromAttribute,
        to: join.toAttribute,
        alias: join.alias,
        "link-type": join.linkType,
      },
      children: [],
    };

    for (const attributeName of join.selectedAttributes) {
      linkEntity.children.push({ name: "attribute", attributes: { name: attributeName }, children: [] });
    }

    const joinFilter = buildTargetFilterNode(join.id, groups, conditions);
    if (joinFilter) {
      linkEntity.children.push(joinFilter);
    }

    attachJoinChildren(linkEntity, join.id, joins, groups, conditions);
    parentNode.children.push(linkEntity);
  }
}

function buildTargetFilterNode(
  target: QueryTarget,
  groups: ConditionGroupDraft[],
  conditions: ConditionDraft[],
): FetchElement | null {
  const targetGroups = groups.filter((group) => group.target === target);
  const topGroups = targetGroups.filter((group) => group.parentGroupId === null);

  if (topGroups.length === 0) {
    return null;
  }

  const nodes = topGroups
    .map((group) => buildGroupFilterNode(group, targetGroups, conditions))
    .filter((node): node is FetchElement => node !== null);

  if (nodes.length === 0) {
    return null;
  }

  if (nodes.length === 1) {
    return nodes[0];
  }

  return {
    name: "filter",
    attributes: { type: "and" },
    children: nodes,
  };
}

function buildGroupFilterNode(
  group: ConditionGroupDraft,
  targetGroups: ConditionGroupDraft[],
  conditions: ConditionDraft[],
): FetchElement | null {
  const groupConditions = conditions
    .filter((condition) => condition.groupId === group.id)
    .map(toConditionNode)
    .filter((node): node is FetchElement => node !== null);

  const childGroups = targetGroups.filter((candidate) => candidate.parentGroupId === group.id);
  const childFilters = childGroups
    .map((child) => buildGroupFilterNode(child, targetGroups, conditions))
    .filter((node): node is FetchElement => node !== null);

  const children = [...groupConditions, ...childFilters];
  if (children.length === 0) {
    return null;
  }

  return {
    name: "filter",
    attributes: { type: group.type },
    children,
  };
}

function toConditionNode(condition: ConditionDraft): FetchElement | null {
  if (!condition.attribute || !condition.operator) {
    return null;
  }

  const operatorSpec = DATAVERSE_OPERATOR_MAP.get(condition.operator);
  if (!operatorSpec) {
    return null;
  }

  const attributes: Record<string, string> = {
    attribute: condition.attribute,
    operator: condition.operator,
  };

  if (condition.valueOfAttribute.trim().length > 0) {
    attributes.valueof = condition.valueOfAttribute.trim();
    return { name: "condition", attributes, children: [] };
  }

  const values = parseConditionValues(condition, operatorSpec.arity);
  if (operatorSpec.arity === "none") {
    return { name: "condition", attributes, children: [] };
  }

  if (operatorSpec.arity === "single") {
    if (values.length !== 1) {
      return null;
    }
    attributes.value = values[0];
    return { name: "condition", attributes, children: [] };
  }

  const children = values.map((item) => ({ name: "value" as const, attributes: {}, children: [], text: item }));
  return { name: "condition", attributes, children };
}

function parseConditionValues(condition: ConditionDraft, arity: "none" | "single" | "multiple" | "exactly-two"): string[] {
  if (arity === "none") {
    return [];
  }

  if (arity === "single") {
    return condition.value.trim().length > 0 ? [condition.value.trim()] : [];
  }

  if (arity === "exactly-two") {
    const values: string[] = [];
    if (condition.value.trim().length > 0) values.push(condition.value.trim());
    if (condition.secondValue.trim().length > 0) values.push(condition.secondValue.trim());
    return values;
  }

  return condition.multiValues
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

export function renderFetchXml(input: BuildFetchXmlInput): string {
  return toFetchXml(buildFetchElement(input));
}

function extractConditionDraft(conditionNode: FetchElement, target: QueryTarget, groupId: string): ConditionDraft {
  const values = conditionNode.children
    .filter((child) => child.name === "value")
    .map((child) => (child.text || "").trim())
    .filter((item) => item.length > 0);

  const attributes = conditionNode.attributes;
  const operator = String(attributes.operator || "eq");
  const arity = DATAVERSE_OPERATOR_MAP.get(operator)?.arity || "single";

  let value = "";
  let secondValue = "";
  let multiValues = "";

  if (arity === "single") {
    value = String(attributes.value || values[0] || "");
  } else if (arity === "exactly-two") {
    value = String(values[0] || attributes.value || "");
    secondValue = String(values[1] || "");
  } else if (arity === "multiple") {
    const payload = values.length > 0 ? values : String(attributes.values || "").split(",");
    multiValues = payload.map((item) => item.trim()).filter((item) => item.length > 0).join(", ");
  }

  return {
    id: makeId("cond"),
    target,
    groupId,
    attribute: String(attributes.attribute || ""),
    operator,
    value,
    secondValue,
    multiValues,
    valueOfAttribute: String(attributes.valueof || ""),
  };
}

function parseFilterTree(
  filterNode: FetchElement,
  target: QueryTarget,
  parentGroupId: string | null,
  groups: ConditionGroupDraft[],
  conditions: ConditionDraft[],
): void {
  const groupId = makeId("group");
  groups.push({
    id: groupId,
    target,
    parentGroupId,
    type: String(filterNode.attributes.type || "and") === "or" ? "or" : "and",
  });

  for (const child of filterNode.children) {
    if (child.name === "condition") {
      conditions.push(extractConditionDraft(child, target, groupId));
      continue;
    }
    if (child.name === "filter") {
      parseFilterTree(child, target, groupId, groups, conditions);
    }
  }
}

function parseJoinNodes(
  parentNode: FetchElement,
  parentTarget: QueryTarget,
  joins: JoinDraft[],
  groups: ConditionGroupDraft[],
  conditions: ConditionDraft[],
): void {
  const joinNodes = parentNode.children.filter((child) => child.name === "link-entity");
  for (const joinNode of joinNodes) {
    const joinId = makeId("join");
    const join: JoinDraft = {
      id: joinId,
      parentTarget,
      relationKey: "",
      alias: String(joinNode.attributes.alias || ""),
      linkType: String(joinNode.attributes["link-type"] || "outer"),
      selectedAttributes: joinNode.children
        .filter((child) => child.name === "attribute")
        .map((child) => String(child.attributes.name || ""))
        .filter((item) => item.length > 0),
      targetEntityLogicalName: String(joinNode.attributes.name || ""),
      fromAttribute: String(joinNode.attributes.from || ""),
      toAttribute: String(joinNode.attributes.to || ""),
    };
    joins.push(join);

    const directConditions = joinNode.children.filter((child) => child.name === "condition");
    if (directConditions.length > 0) {
      const groupId = makeId("group");
      groups.push({ id: groupId, target: joinId, parentGroupId: null, type: "and" });
      directConditions.forEach((conditionNode) => {
        conditions.push(extractConditionDraft(conditionNode, joinId, groupId));
      });
    }

    joinNode.children
      .filter((child) => child.name === "filter")
      .forEach((filterNode) => parseFilterTree(filterNode, joinId, null, groups, conditions));

    parseJoinNodes(joinNode, joinId, joins, groups, conditions);
  }
}

export function parseFetchXmlToDraft(xml: string): ParseFetchXmlDraftResult {
  const parsed = fromFetchXml(xml);
  const rootEntityNode = parsed.children.find((child) => child.name === "entity");
  if (!rootEntityNode) {
    throw new Error("The provided FetchXML does not contain a root entity.");
  }

  const rootEntityLogicalName = String(rootEntityNode.attributes.name || "");
  if (!rootEntityLogicalName) {
    throw new Error("The provided FetchXML root entity is missing a name attribute.");
  }

  const rootSelectedAttributes = rootEntityNode.children
    .filter((child) => child.name === "attribute")
    .map((child) => String(child.attributes.name || ""))
    .filter((item) => item.length > 0);

  const conditionGroups: ConditionGroupDraft[] = [];
  const conditions: ConditionDraft[] = [];

  const rootDirectConditions = rootEntityNode.children.filter((child) => child.name === "condition");
  if (rootDirectConditions.length > 0) {
    const groupId = makeId("group");
    conditionGroups.push({ id: groupId, target: "root", parentGroupId: null, type: "and" });
    rootDirectConditions.forEach((conditionNode) => {
      conditions.push(extractConditionDraft(conditionNode, "root", groupId));
    });
  }

  rootEntityNode.children
    .filter((child) => child.name === "filter")
    .forEach((filterNode) => parseFilterTree(filterNode, "root", null, conditionGroups, conditions));

  if (conditionGroups.filter((group) => group.target === "root" && group.parentGroupId === null).length === 0) {
    conditionGroups.push({
      id: makeId("group"),
      target: "root",
      parentGroupId: null,
      type: "and",
    });
  }

  const joins: JoinDraft[] = [];
  parseJoinNodes(rootEntityNode, "root", joins, conditionGroups, conditions);

  return {
    rootEntityLogicalName,
    rootSelectedAttributes,
    joins,
    conditionGroups,
    conditions,
  };
}
